import _ from 'lodash';
import { mapActions, mapState, mapGetters } from 'vuex';
import { adapi, Timeline, Comment, permissions, CreativeRefresh } from '~';
import { COMMENTABLE_TYPES, FLOWS, NOTIFICATION_EVENTS, STATUSES } from '@/util/constants';
import Creatives from '@/components/campaign-creatives/CampaignCreatives.vue';
import Status from '@/components/campaign-status/CampaignStatus.vue';
import CampaignInfo from '@/components/campaign-info/CampaignInfo.vue';
import CampaignHeading from '@/components/campaign-heading/CampaignHeading.vue';
import CampaignStatistics from '@/components/campaign-statistics/CampaignStatistics.vue';
import CustomerInfo from '@/components/customer-info/CustomerInfo.vue';
import SpecialAdCategory from '@/components/special-ad-category/SpecialAdCategory.vue';
import FacebookPageBox from '@/components/facebook-page-box/FacebookPageBox.vue';
import LandingPageBox from '@/components/landing-page-box/LandingPageBox.vue';
import StartDateBox from '@/components/start-date-box/StartDateBox.vue';
import SuspendResumeBox from '@/components/suspend-resume-box/SuspendResumeBox.vue';
import LeadContactBox from '@/components/lead-contact-box/LeadContactBox.vue';
import AutoPublishBox from '@/components/auto-publish-box/AutoPublishBox.vue';
import CreativeLogs from '@/components/lab/creative-logs/CreativeLogs.vue';
import TriggerNotification from '@/components/trigger-notification/TriggerNotification.vue';

export default {
  components: {
    CreativeLogs,
    CreativeRefresh,
    TriggerNotification,
    SpecialAdCategory,
    comment: Comment,
    timeline: Timeline,
    creatives: Creatives,
    'campaign-status': Status,
    'campaign-info': CampaignInfo,
    'campaign-heading': CampaignHeading,
    'campaign-statistics': CampaignStatistics,
    'customer-info': CustomerInfo,
    'facebook-page-box': FacebookPageBox,
    'landing-page-box': LandingPageBox,
    'start-date-box': StartDateBox,
    'auto-publish-box': AutoPublishBox,
    'suspend-resume-box': SuspendResumeBox,
    'lead-contact-box': LeadContactBox,
  },
  data() {
    return {
      addingComment: false,
      customAudienceOptOut: false,
      NOTIFICATION_EVENTS,
      cancelLoading: false,
    };
  },
  computed: {
    ...mapGetters('campaign', {
      campaign: 'currentCampaign',
    }),
    ...mapState('campaign', {
      loading: 'loading',
      error: 'error',
    }),
    ...mapState('auth', { currentUser: 'user' }),
    permissions() {
      return permissions.getPermissions(this.campaign);
    },
    customer() {
      return _.get(this.campaign, 'customer', null);
    },
    showFbButton() {
      return this.campaign.direct_link &&
        this.isPublished && (
          _.get(this.campaign, 'status.code', '') === 'ONL' ||
          _.get(this.campaign, 'status.code', '') === 'FIN'
        );
    },
    canPublish() {
      return permissions.canGlobal(this.currentUser, 'Campaign', ['seeAutopublish']);
    },
    canCancelCampaign() {
      return permissions.getPermissions(this.campaign).includes('cancelCampaign');
    },
    canSeeDetailedResponse() {
      return permissions.canGlobal(this.currentUser, 'Campaign', ['viewDetailedPublishReponse']);
    },
    canSuspendResumeCampaign() {
      return this.permissions.includes('suspend') || this.permissions.includes('resume');
    },
    isPublished() {
      const satRequest = _.get(this.campaign, 'sat_requested_at', false);

      return satRequest && satRequest !== '0000-00-00 00:00:00';
    },
    responseWaiting() {
      return (this.campaign.sat_success_response == null ||
        this.campaign.sat_responded_at == null) &&
        this.campaign.sat_requested_at;
    },
    unpublishedChanges() {
      const ubpublished = this.campaign.unpublished_changes;
      if (!ubpublished || ubpublished === '' || ubpublished === 'null') {
        return [];
      }
      return this.campaign.unpublished_changes.map(item => this.$t(`campaign_field.title.${item}`));
    },
    responseOK() {
      return this.campaign.sat_success_response === 1 &&
        this.campaign.sat_responded_at;
    },
    hasUnfinishedIteration() {
      let iterations = this.campaign.iterations;
      if (this.campaign.status.code === STATUSES.FIN) {
        iterations = iterations.filter(iteration => iteration.finished_at === null);
        return iterations.length > 0;
      }
      return false;
    },
    responseKO() {
      return this.campaign.sat_success_response === 0 &&
        this.campaign.sat_responded_at &&
        this.campaign.publish_error_response;
    },
    statsVisible() {
      return ['FIN', 'ONL'].includes(this.campaign.baseStatus.code);
    },
    comment() {
      return {
        commentable_id: this.campaign.id,
        commentable_type: COMMENTABLE_TYPES.CAMPAIGN,
      };
    },
    isASAP() {
      return _.get(this.campaign, 'status_flow.tag', null) === FLOWS.ASAP;
    },
    canViewTimeline() {
      return permissions.canGlobal(this.currentUser, 'Campaign', ['viewTimeline']);
    },
    creativeLogs() {
      return _.get(this.campaign, 'creative_logs', []);
    },
    disapprovalMessages() {
      const disapproved = _.get(this, 'campaign.issues', [])
        .filter(i => !i.finished_at)
        .map(i => i.name)
        .find(i => i === 'disapproved');

      if (!disapproved) {
        return null;
      }

      return _.get(this, 'campaign.details', [])
        .filter(d => d.name === 'fb-status-message')
        .map(d => JSON.parse(d.value))[0] || null;
    },
  },
  methods: {
    ...mapActions('campaign', ['reloadCampaign']),
    publishCampaign() {
      return adapi.post(`campaigns/${this.campaign.code}/publish`)
        .then(() => {
          // TODO: should we reload campaign data after this or do something else?
          this.$toast.success({ message: this.$t('pos.campaign.campaign-published') });
        })
        .catch(error => {
          this.$toast.error({ message: error });
        });
    },
    toggleCustomAudience() {
      return adapi.post(`campaigns/${this.campaign.code}/custom-audience/toggle`)
        .then(() => {
          this.$toast.success({ message: this.$t('pos.campaign.toggle-success') });
          this.customAudienceOptOut = true;
        })
        .catch(error => {
          this.$toast.error({ message: error });
        });
    },
    cancelCampaign() {
      this.cancelLoading = true;
      return adapi.post(`campaigns/${this.campaign.code}/cancel`)
        .then(result => {
          if (result) {
            this.$toast.success({ message: this.$t('pos.campaign.campaign-cancelled') });
          } else {
            this.$toast.warn({ message: this.$t('pos.campaign.campaign-cancel-error') });
          }
          this.cancelLoading = false;
        })
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          this.cancelLoading = false;
        });
    },
    updateComment() {
      this.addingComment = true;
      return this.$refs.comment.updateComment()
        .then(() => {
          this.$toast.success({ message: this.$t('pos.comment.comment-added') });
          this.addingComment = false;
        })
        .then(() => this.reloadCampaign())
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          this.addingComment = false;
        });
    },
  },
  mounted() {
    if (this.campaign.details) {
      const customAudienceOptOut = this.campaign.details.filter(arr => arr.name === 'customAudienceOptOut')[0];
      this.customAudienceOptOut = _.get(customAudienceOptOut, 'value', '0') === '1';
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/lab/campaign-step/CampaignStep.js